import { OnInit, Component, ViewChild } from "@angular/core";
import { UserService } from "@app/services/user.service";
import { User } from "@app/data/user";
import { Router } from "@angular/router";
import { ToastService } from "@app/services/toast.service";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import {
  ImageUploaderModule,
  RemoteDataService,
} from "@nowinstore/nowinstore-core-ui/components";
import {
  Catalog,
  Cell,
  ElementType,
  Page,
  PageLayout,
} from "@app/data/catalog";
import * as _ from "lodash";
import {
  CATALOG_API_URL,
  GLOBAL_SETTINGS_API_URL,
  LISTING_API_URL,
  PAGE_LAYOUT_API_URL,
  TEMPLATE_API_URL,
  TOOL_API_URL,
  USER_API_URL,
} from "@app/data/const";
import { Listing } from "@app/data/listing";
import { CssService } from "@app/services/css.service";
import {
  TemplateDefinition,
  TemplateLoaderService,
} from "@app/services/template-loader.service";
import { MessageService, TreeTableNode } from "primeng/api";
import { WindowService } from "@app/services/window.service";
import { Galleria } from "primeng/galleria";
import { CommonModule } from "@angular/common";
import { ColorSelectorComponent } from "../catalog-editor/color-selector.component";
import {
  WizardModule,
  NISTemplateModule,
} from "@nowinstore/nowinstore-core-ui/components";
import { OptionSelectorComponent } from "./option-selector.component";
import { InputTextModule } from "primeng/inputtext";
import { DialogModule } from "primeng/dialog";
import { ProgressBarModule } from "primeng/progressbar";
import { SelectionIndicatorComponent } from "./selection-indicator.component";
import { CatalogListingsEditorComponent } from "../listings-picker/catalog-listings-editor.component";
import { ButtonModule } from "primeng/button";
import { AssetsPickerComponent } from "../catalog-editor/assets-picker.component";
import { TabViewModule } from "primeng/tabview";
import { FormsModule } from "@angular/forms";
import { BrandEditorComponent } from "../catalog-editor/brand-editor.component";
import { ToastModule } from "primeng/toast";

declare var $zoho: any;

interface SiteInformation {
  title: string;
  description: string;
  image: string;
  site_name: string;
  favicon: string;
}

enum PageLayoutType {
  text = "fullTextPage",
}

interface ReplacementMap {
  [key: string]: string;
}
@Component({
  standalone: true,
  imports: [
    CommonModule,
    WizardModule,
    ColorSelectorComponent,
    OptionSelectorComponent,
    CatalogListingsEditorComponent,
    ButtonModule,
    SelectionIndicatorComponent,
    AssetsPickerComponent,
    TabViewModule,
    InputTextModule,
    DialogModule,
    ProgressBarModule,
    FormsModule,
    BrandEditorComponent,
    NISTemplateModule,
    ImageUploaderModule,
    ToastModule,
  ],
  templateUrl: "./catalog-add.component.html",
  providers: [MessageService],
  styles: [
    `
      :host ::ng-deep .p-tabview .p-tabview-panels {
        background-color: transparent;
      }
      :host ::ng-deep .p-tabview-nav-content {
        text-align: center;
      }
      :host ::ng-deep .p-tabview-nav {
        min-width: auto;
      }
      :host ::ng-deep .p-tabview-nav .tab-counter {
        color: white;
        padding: 0.125rem 0.5rem;
        font-size: 0.875rem;
      }
      :host ::ng-deep .p-tabview-nav .p-highlight .tab-counter {
        background-color: #00a5cf;
      }
      :host ::ng-deep .p-tabview-nav .tab-counter {
        background-color: #dee2e6;
      }
    `,
  ],
})
export class CatalogAddComponent implements OnInit {
  user: User;
  answers: {
    usage: string;
    wholesale_percentage: number;
    template: TemplateDefinition;
    template_catalog: Catalog;
    selection: TreeTableNode[];
    front_cover: string;
    branding_css: string;
  } = {
    usage: "",
    wholesale_percentage: 50,
    template: null,
    template_catalog: null,
    selection: [],
    front_cover: null,
    branding_css: null,
  };
  purposes: any = [
    {
      icon: "fa-sharp fa-people-group",
      value: "retail",
      label: $localize`Retail`,
    },
    {
      icon: "fa-sharp fa-store",
      value: "wholesale",
      label: $localize`Wholesale`,
      showValue: true,
      labelValue: "% OFF",
    },
  ];
  // templates: Catalog[] = [];
  loading: boolean = false;
  generating: boolean;
  generationProgress: string;
  templates: string[] = [];
  templateDefinitions: TemplateDefinition[] = [];
  cssVariables: { [key: string]: string } = {};
  categories: Record<string, any> = {
    jewelry: {
      label: $localize`Jewelry`,
      icon: "fa-gem",
    },
    sportswear: {
      label: $localize`Sportswear`,
      icon: "fa-person-running",
    },
    swimsuit: {
      label: $localize`Swimsuit`,
      icon: "fa-person-swimming",
    },
    adult_shop: {
      label: $localize`Adult Shop`,
      icon: "fa-duck",
    },
    t_shirt: {
      label: $localize`T-shirt`,
      icon: "fa-shirt",
    },
    art: {
      label: $localize`Art & Photography`,
      icon: "fa-paint-brush",
    },
    health_beauty: {
      label: $localize`Health & Beauty`,
      icon: "fa-soap",
    },
    craft_stationery: {
      label: $localize`Craft & stationery`,
      icon: "fa-memo-pad",
    },
  };
  ref: DynamicDialogRef | undefined;
  showPreview: boolean = false;
  previews: string[] = [];
  landingPageAssets: string[] = [];
  storeAssets: string[] = [];
  platform: string;
  maxPages: number = 500;
  limit: number;
  isOverLimit: boolean = false;
  constructor(
    private templateLoaderService: TemplateLoaderService,
    private userService: UserService,
    private remoteDataService: RemoteDataService,
    private windowService: WindowService,
    private messageService: MessageService
  ) {}

  async ngOnInit(): Promise<void> {
    this.user = await this.remoteDataService.get<User>(USER_API_URL);
    this.limit = this.user.subscription.plan.limits.max_products_per_catalog;
    this.platform = this.user.accounts[0].type;
    const settings = await this.remoteDataService.get<any>(
      GLOBAL_SETTINGS_API_URL
    );
    const setting = settings.filter(
      (setting) => setting.key === "catalog_maker_templates"
    )[0];
    this.templates = setting.parameters.urls;

    for (let template of this.templates) {
      try {
        this.templateDefinitions.push(
          await this.templateLoaderService.loadTemplateDefinition(template)
        );
      } catch (e) {
        console.error(e);
      }
    }
    this.templateDefinitions.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }
  escapeJSONString(jsonString: string): string {
    return jsonString
      .replace(/\\/g, "\\\\") // Escape backslashes
      .replace(/"/g, '\\"') // Escape double quotes
      .replace(/\n/g, "\\n") // Escape new lines
      .replace(/\r/g, "\\r") // Escape carriage returns
      .replace(/\t/g, "\\t"); // Escape tabs
  }

  generateTableOfContents(headers: { title: string; page: string }[]): string {
    var content = '<ul class=\\"toc-index nis-toc-index\\">';
    headers.forEach(function (pageHeader) {
      content += '<li class=\\"toc-index-item nis-toc-index-item\\">';
      content += '<span class=\\"toc-page-title nis-toc-page-title\\">';
      content += pageHeader.title;
      content += "</span>";
      content +=
        '<span class=\\"toc-page-delimiter nis-toc-page-delimiter\\">&nbsp;</span>';
      content += '<span class=\\"toc-page-number nis-toc-page-number\\">';
      content += pageHeader.page;
      content += "</span>";
      content += "</li>";
    });
    content += "</ul>";
    return content;
  }
  replaceKeywordsInJson(jsonData: any, replacements: ReplacementMap): any {
    // Convert JSON data to string for keyword replacement
    let jsonString = JSON.stringify(jsonData, null, 2);

    // Replace keywords with the provided strings
    for (const [key, value] of Object.entries(replacements)) {
      const regex = new RegExp(key, "g");
      jsonString = jsonString.replace(
        regex,
        this.escapeJSONString(value || "")
      );
    }

    // Parse the modified string back to JSON
    const updatedJsonData = JSON.parse(jsonString);

    return updatedJsonData;
  }
  assignToLargestImageCell(page: Page, url: string) {
    let index = -1;
    let maxRegion = 0;
    (<PageLayout>page.layout).cells.forEach((cell, i) => {
      if (
        cell.accepts.includes(ElementType.Image) &&
        cell.width * cell.height > maxRegion &&
        !cell.lock
      ) {
        index = i;
        maxRegion = cell.width * cell.height;
      }
    });
    if (index > -1) {
      const element = page.elements[index];
      element.properties = element.properties || {};
      element.type = ElementType.Image;
      element.size = "cover";
      element.properties.url = url;
    }
  }
  assignToSmallestImageCell(page: Page, url: string) {
    let index = -1;
    let minRegion = Infinity;
    (<PageLayout>page.layout).cells.forEach((cell, i) => {
      if (
        cell.accepts.includes(ElementType.Image) &&
        cell.width * cell.height < minRegion &&
        !cell.lock
      ) {
        index = i;
        minRegion = cell.width * cell.height;
      }
    });
    if (index > -1) {
      const element = page.elements[index];
      element.properties = element.properties || {};
      element.type = ElementType.Image;
      element.size = "contain";
      element.properties.url = url;
    }
  }
  assignToLargestTextCell(page: Page, html: string) {
    let index = -1;
    let maxRegion = 0;
    (<PageLayout>page.layout).cells.forEach((cell, i) => {
      if (
        cell.accepts.includes(ElementType.Text) &&
        cell.width * cell.height > maxRegion
      ) {
        index = i;
        maxRegion = cell.width * cell.height;
      }
    });
    if (index > -1) {
      const element = page.elements[index];
      element.properties = element.properties || {};
      element.properties.html = html;
    }
  }
  replaceMetaTags(page: Page, siteInformation: Partial<SiteInformation>) {
    const logoUrl =
      this.user.business_profile.logo_url || siteInformation.image;
    const elements = page.elements.filter(
      (element) => element.type === ElementType.Text
    );
    for (let element of elements) {
      element.properties.html = element.properties.html.replace(
        "{{shop.brand.logo_url}}",
        logoUrl
      );
    }
  }

  async surveyCompleted() {
    if (this.isOverLimit) {
      this.messageService.add({
        severity: "error",
        summary: $localize`Maximum number of products`,
        detail: $localize`You have reached the maximum number of products permitted on your plan. Please remove some products from your selection.`,
      });
      return;
    }
    this.generating = true;
    //create cover page
    this.generationProgress = $localize`Generating front cover page...`;
    let siteInformation: Partial<SiteInformation> = {};
    try {
      siteInformation = await this.remoteDataService.get<SiteInformation>(
        TOOL_API_URL,
        `store-information?url=${encodeURIComponent(this.user.shop_url)}`
      );
    } catch (e) {
      console.error(e);
    }

    const logoUrl =
      this.user.business_profile.logo_url || siteInformation.image;

    //create front cover
    const templateCatalog = this.answers.template_catalog;
    const newCatalog: Catalog = {
      title: "Catalog",
      pages: [],
      version: 3,
      default_price_template: templateCatalog.default_price_template,
      default_images_style: templateCatalog.default_images_style,
      default_price_template_style:
        templateCatalog.default_price_template_style,
      default_footer: new URL(this.user.shop_url).hostname,
      font_links: [],
      branding_css: this.answers.branding_css,
      custom_css: templateCatalog.custom_css.replace(
        "{{shop.brand.logo_url}}",
        logoUrl
      ),
      settings: {
        ...templateCatalog.settings,
        public: this.answers.usage !== "wholesale",
      },
    };

    //new catalog price template
    newCatalog.default_price_template.html =
      this.answers.usage === "wholesale"
        ? this.answers.template.wholesale_caption
        : this.answers.template.retail_caption;

    //front cover
    if (logoUrl?.length > 0) {
      this.assignToSmallestImageCell(templateCatalog.pages[0], logoUrl);
    }
    if (this.answers.front_cover?.length > 0) {
      this.assignToLargestImageCell(
        templateCatalog.pages[0],
        this.answers.front_cover
      );
    }
    this.replaceMetaTags(templateCatalog.pages[0], siteInformation);
    newCatalog.pages.push(templateCatalog.pages[0]);

    //about us
    this.generationProgress = $localize`Generating about us page...`;
    const aboutUsPage = templateCatalog.pages[1];
    this.assignToLargestTextCell(
      aboutUsPage,
      '<p>{{shop.description}}</p><br/><br/><p style="text-align: right"><img height="32" src="{{shop.brand.logo_url}}"/></p>'
        .replace("{{shop.description}}", siteInformation.description || "")
        .replace("{{shop.brand.logo_url}}", logoUrl)
    );
    newCatalog.pages.push(aboutUsPage);

    //toc
    this.generationProgress = $localize`Generating table of contents page...`;
    newCatalog.pages.push(templateCatalog.pages[2]);

    //products page
    this.generationProgress = $localize`Generating products pages...`;
    const productsPageLayout = <PageLayout>templateCatalog.pages[3].layout;
    const nbProductsPerPage = productsPageLayout.cells.filter((cell) =>
      cell.accepts.includes(ElementType.Product)
    ).length;
    var pageHeaders = [];

    //wait for all categories to be loaded
    while (this.answers.selection.some((node) => !(<any>node).loaded)) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      //display the number of categories being loaded
      this.generationProgress = $localize`Generating products pages... (${
        this.answers.selection.filter((node) => !(<any>node).loaded).length
      } categories remaining)`;
    }
    for (let category of this.answers.selection) {
      pageHeaders.push({
        title: category.data.title,
        page: templateCatalog.pages.length + 1,
      });
      const products = category.children;
      if (products?.length > 0) {
        //create a page for each group of products
        for (let i = 0; i < products.length; i += nbProductsPerPage) {
          const page = {
            layout: productsPageLayout,
            header: {
              properties: {
                html: this.answers.template.default_header.replace(
                  "{{collection.title}}",
                  category.data.title
                ),
              },
            },
            footer: {
              properties: {
                html: new URL(this.user.shop_url).hostname,
              },
            },
            kind: "products",
            showHeadersFooters: true,
            elements: [],
          };
          for (let j = 0; j < nbProductsPerPage; j++) {
            const product = products[i + j];
            if (!product) {
              break;
            }
            if (this.answers.usage === "wholesale") {
              product.data.price =
                product.data.price *
                (1 - this.answers.wholesale_percentage / 100);
            }
            page.elements.push({
              type: ElementType.Product,
              properties: product.data,
            });
          }
          newCatalog.pages.push(page);
        }
      }
    }

    //back cover
    this.generationProgress = $localize`Generating back cover page...`;
    const backCoverPage =
      templateCatalog.pages[templateCatalog.pages.length - 1];
    const replacements: ReplacementMap = {
      "{{shop.email}}": this.user.business_profile.contact_email,
      "{{shop.phone}}": this.user.business_profile.phone_number,
      "{{shop.name}}": this.user.business_profile.name,
      "{{shop.default_address.address1}}": this.user.business_profile.address,
      "{{shop.default_address.address2}}": this.user.business_profile.address2,
      "{{shop.default_address.city}}": this.user.business_profile.city,
      "{{shop.default_address.province_code}}":
        this.user.business_profile.state_province,
      "{{shop.default_address.zip}}": this.user.business_profile.zip_code,
    };
    let backPageDescription =
      "<p><strong>Email:</strong> {{shop.email}}<br/><strong>Phone:</strong> {{shop.phone}}<br/><br/><strong>ADDRESS</strong><br/><br/>{{shop.name}}<br/>{{shop.default_address.address1}} {{shop.default_address.address2}}, {{shop.default_address.city}}, {{shop.default_address.province_code}} {{shop.default_address.zip}}</p>";
    for (let [key, value] of Object.entries(replacements)) {
      backPageDescription = backPageDescription.replace(key, value || "");
    }
    this.assignToLargestTextCell(backCoverPage, backPageDescription);
    newCatalog.pages.push(backCoverPage);

    this.generating = false;
    if (newCatalog.pages.length > this.maxPages) {
      this.messageService.add({
        severity: "error",
        summary: $localize`Maximum number of pages`,
        detail: $localize`Your catalog has ${newCatalog.pages.length} pages. You have reached the maximum number of ${this.maxPages} pages permitted on our App. Please remove some products from your selection.`,
      });
      return;
    }
    const createdCatalog = await this.remoteDataService.post<Catalog>(
      CATALOG_API_URL,
      newCatalog
    );
    this.windowService.getNativeWindow().document.location.href =
      "/designs/" + createdCatalog._id + "/edit";
  }

  async setTheme(templateDefinition: TemplateDefinition) {
    this.answers.template = templateDefinition;
    this.answers.template_catalog = await this.remoteDataService.get<Catalog>(
      CATALOG_API_URL,
      templateDefinition.template
    );

    this.answers.branding_css = this.answers.template_catalog.branding_css;
  }

  getSecondaryTitle(isOverLimit: boolean) {
    return isOverLimit ? $localize`Limit reached` : "";
  }
}
